import axios, { AxiosInstance, AxiosResponse } from 'axios';

// Token storage — purely in-memory so tokens are cleared on page refresh/close.
// This ensures users are signed out whenever the page is refreshed or the tab is reopened.
class TokenStore {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private _loggingOut = false;

  constructor() {
    // Intentionally NOT restoring from sessionStorage — forces re-login on refresh
    // Clear any stale tokens left over from previous sessions
    try {
      sessionStorage.removeItem('mp_at');
      sessionStorage.removeItem('mp_rt');
    } catch {
      // sessionStorage not available
    }
  }

  setTokens(access: string, refresh: string): void {
    this.accessToken = access;
    this.refreshToken = refresh;
    this._loggingOut = false;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  setAccessToken(token: string): void {
    this.accessToken = token;
  }

  /** Returns true while a logout is in progress — prevents the 401 interceptor from refreshing. */
  get isLoggingOut(): boolean {
    return this._loggingOut;
  }

  clear(): void {
    this._loggingOut = true;
    this.accessToken = null;
    this.refreshToken = null;
    // Also clear sessionStorage in case an older code version persisted tokens there
    try {
      sessionStorage.removeItem('mp_at');
      sessionStorage.removeItem('mp_rt');
    } catch { /* ignore */ }
  }
}

export const tokenStore = new TokenStore();

// In-memory CSRF token storage
let csrfToken: string | null = null;
let csrfFetchPromise: Promise<void> | null = null;

// Fetch CSRF token from the backend
const fetchCsrfToken = async (baseURL: string): Promise<void> => {
  try {
    const response = await axios.get(`${baseURL}/csrf-token`, {
      withCredentials: true,
    });
    csrfToken = response.data.csrfToken;
  } catch (error) {
    console.warn('Failed to fetch CSRF token');
  }
};

// Ensure CSRF token is available (fetches once, reuses promise for concurrent calls)
const ensureCsrfToken = async (baseURL: string): Promise<void> => {
  if (csrfToken) return;
  if (!csrfFetchPromise) {
    csrfFetchPromise = fetchCsrfToken(baseURL).finally(() => {
      csrfFetchPromise = null;
    });
  }
  await csrfFetchPromise;
};

// Create axios instance
const apiUrl = process.env.REACT_APP_API_URL;
if (!apiUrl) {
  throw new Error('REACT_APP_API_URL environment variable is required');
}
const api: AxiosInstance = axios.create({
  baseURL: apiUrl,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add auth token and CSRF token
api.interceptors.request.use(
  async (config) => {
    const token = tokenStore.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add CSRF token for state-changing requests
    const method = config.method?.toUpperCase();
    if (method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      if (!csrfToken) {
        await ensureCsrfToken(config.baseURL || api.defaults.baseURL || '');
      }
      if (csrfToken) {
        config.headers['X-XSRF-Token'] = csrfToken;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Don't attempt refresh if a logout is in progress
      if (tokenStore.isLoggingOut) {
        return Promise.reject(error);
      }

      try {
        const refreshToken = tokenStore.getRefreshToken();
        if (refreshToken) {
          const response = await axios.post(`${api.defaults.baseURL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data;
          tokenStore.setAccessToken(accessToken);
          // Store rotated refresh token if provided
          if (newRefreshToken) {
            tokenStore.setTokens(accessToken, newRefreshToken);
          }

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } else {
          // No refresh token available — redirect to login
          tokenStore.clear();
          window.location.href = '/login';
          return Promise.reject(error);
        }
      } catch (refreshError) {
        tokenStore.clear();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle CSRF token expiration - re-fetch and retry
    if (error.response?.status === 403 &&
        error.response?.data?.error?.message === 'CSRF token validation failed' &&
        !originalRequest._csrfRetry) {
      originalRequest._csrfRetry = true;
      csrfToken = null; // Clear stale token
      await ensureCsrfToken(api.defaults.baseURL || '');
      if (csrfToken) {
        originalRequest.headers['X-XSRF-Token'] = csrfToken;
        return api(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

// Pre-fetch CSRF token on module load
ensureCsrfToken(apiUrl);

// Auth API (mapped to Express backend routes)
export const authAPI = {
  login2FA: (payload: { challengeToken: string; code: string }) =>
    api.post('/auth/2fa/login', payload),
  login: (credentials: { email: string; password: string }) =>
    api.post('/auth/login', credentials),

  register: (userData: {
    name: string;
    email: string;
    phoneNumber: string;
    password: string;
    confirmPassword: string;
  }) => api.post('/auth/register', userData),

  verifyEmail: (token: string) =>
    api.post('/auth/verify-email', { token }),

  resendVerification: (email: string) =>
    api.post('/auth/resend-verification', { email }),

  logout: () => api.post('/auth/logout'),

  checkProfileCompletion: () =>
    api.get('/auth/profile-completion'),

  refreshToken: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),

  me: () => api.get('/auth/me'),
};

// Cards API (mapped to Express backend routes)
export const cardsAPI = {
  getCards: () => api.get('/cards'),

  setDefaultCard: (cardId: string) =>
    api.put(`/cards/${cardId}/set-default`),

  deleteCard: (cardId: string, pin?: string) =>
    api.delete(`/cards/${cardId}`, { data: pin ? { pin } : undefined }),

  initializeCardLink: (callbackUrl?: string) =>
    api.post('/cards/initialize', callbackUrl ? { callback_url: callbackUrl } : {}),

  verifyCardLink: (reference: string) =>
    api.post('/cards/verify', { reference }),
};

// Bank Accounts API (mapped to Express backend routes)
export const bankAccountsAPI = {
  getBanks: () => api.get('/bank-accounts/banks'),

  getBankAccounts: () => api.get('/bank-accounts'),

  resolveBankAccount: (payload: {
    accountNumber: string;
    bankCode?: string;
  }) => api.post('/bank-accounts/resolve', payload),

  addBankAccount: (accountData: {
    accountNumber: string;
    bankCode: string;
    accountName?: string;
    bankName?: string;
  }) => api.post('/bank-accounts', accountData),

  setDefaultBankAccount: (accountId: string) =>
    api.put(`/bank-accounts/${accountId}/set-default`),

  deleteBankAccount: (accountId: string, pin?: string) =>
    api.delete(`/bank-accounts/${accountId}`, { data: pin ? { pin } : undefined }),
};

// Payments API (mapped to Express backend routes)
/** One key per transfer attempt; reused when the same attempt is retried after a network failure. */
export const newIdempotencyKey = (): string =>
  `web-${(globalThis.crypto && 'randomUUID' in globalThis.crypto) ? globalThis.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`}`;

export const paymentsAPI = {
  transfer: (transferData: {
    recipientEmail?: string;
    recipientPhone?: string;
    amount: number;
    currency: string;
    settlementCurrency?: 'NGN' | 'USD';
    description?: string;
    pin?: string;
    otp?: string;
  }, idempotencyKey?: string) =>
    api.post('/payments/transfer', transferData, idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined),

  merchantInit: (payload: {
    email: string;
    password: string;
    amount: number;
    currency: string;
    settlementCurrency?: 'NGN' | 'USD';
    description?: string;
    merchantRef?: string;
  }) => api.post('/payments/merchant/init', payload),

  merchantVerifyOtp: (payload: {
    reference: string;
    otp: string;
  }) => api.post('/payments/merchant/verify-otp', payload),

  merchantSubmitPin: (payload: {
    reference: string;
    pin: string;
  }) => api.post('/payments/merchant/submit-pin', payload),

  getTransactions: (params?: {
    page?: number;
    limit?: number;
    type?: string;
    status?: string;
  }) => api.get('/payments/transactions', { params }),

  getTransactionById: (transactionId: string) =>
    api.get(`/payments/transactions/${transactionId}`),
};

// Transactions API (alias for payments API)
export const transactionsAPI = {
  getTransactions: (params?: {
    page?: number;
    limit?: number;
    type?: string;
    status?: string;
  }) => api.get('/payments/transactions', { params }),

  getTransactionById: (transactionId: string) =>
    api.get(`/payments/transactions/${transactionId}`),
};

// Users API (mapped to Express backend routes)
export const usersAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data: { firstName?: string; lastName?: string; phoneNumber?: string }) =>
    api.put('/users/profile', data),
  uploadProfileImage: (file: File) => {
    const formData = new FormData();
    formData.append('profileImage', file);
    return api.post('/users/profile/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.post('/auth/change-password', data),
  getQrCode: () => api.get('/users/qr'),
  resolvePublicUser: (payload: { email?: string; phoneNumber?: string }) =>
    api.post('/users/resolve', payload),
};

// Public (unauthenticated) API
export const publicAPI = {
  joinMerchantWaitlist: (payload: {
    businessName: string;
    contactName: string;
    email: string;
    phone?: string;
    businessType?: string;
    message?: string;
  }) => api.post('/public/merchant-waitlist', payload),
};

// Transfer PIN API
export const transferPinAPI = {
  getStatus: () => api.get('/auth/transfer-pin/status'),
  setPin: (pin: string) => api.post('/auth/transfer-pin/set', { pin }),
  changePin: (currentPin: string, newPin: string) =>
    api.post('/auth/transfer-pin/change', { currentPin, newPin }),
  forgotPin: () => api.post('/auth/transfer-pin/forgot'),
  resetPin: (otp: string, newPin: string) =>
    api.post('/auth/transfer-pin/reset', { otp, newPin }),
  verifyPin: (pin: string) => api.post('/auth/transfer-pin/verify', { pin }),
  sendTransferOTP: () => api.post('/auth/transfer-otp/send'),
};


// KYC API
export const kycAPI = {
  getStatus: () => api.get('/kyc/status'),

  getDocuments: () => api.get('/kyc/documents'),

  uploadDocument: (formData: FormData) =>
    api.post('/kyc/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deleteDocument: (documentId: string) =>
    api.delete(`/kyc/documents/${documentId}`),
};

// Notifications API
export const notificationsAPI = {
  getNotifications: (params?: {
    page?: number;
    limit?: number;
  }) => api.get('/notifications', { params }),

  getUnreadCount: () => api.get('/notifications/unread-count'),

  markAsRead: (notificationId: string) =>
    api.put(`/notifications/${notificationId}/read`),

  markAllAsRead: () => api.put('/notifications/read-all'),

  deleteNotification: (notificationId: string) =>
    api.delete(`/notifications/${notificationId}`),
};

export default api;
